import { HttpClient } from '@angular/common/http';
import {distinctUntilChanged, map, share} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {getMessageFromException} from '../utilities/Util';
import {MassAssignPledgeTypeParams, PagedListOfPledgeType, PledgeType, PledgeTypeClient} from './api.client';
import {NotificationService} from './NotificationService';
import {BehaviorSubject, Observable} from 'rxjs';
import {StateResetRegistry} from './state-reset-registry.service';

// 003-retire-ngrx (US3): the pledgeType slice moved off NgRx into this service.
// allPledgeTypes is the app's clearest reference-data cache — loaded once and
// read by 20+ dropdown/checkbox consumers across pledge/agency/company/
// allocation pages — so the slice stays shared. add/update/delete refresh the
// cache so those consumers never see stale fund types after an admin edit.
// The old slice also carried isPledgeTypesLoading, which had no selector
// (write-only) — not ported. The PAGED search results are NOT held here:
// their only consumer is PledgeTypeListComponent, whose search criteria live
// on the component — service-held results outlived the criteria that produced
// them (same class of bug as the other admin lists).
interface PledgeTypeSliceState {
    currentSelectedPledgeType: PledgeType;
    allPledgeTypes: PledgeType[];
}

const initialState: PledgeTypeSliceState = {
    currentSelectedPledgeType: null,
    allPledgeTypes: null
};

@Injectable()
export class PledgeTypeService {

    private readonly _state$ = new BehaviorSubject<PledgeTypeSliceState>(initialState);

    // ---------------- Streams (former pledgeType selectors) ----------------
    readonly allPledgeTypes$ = this.select((s) => s.allPledgeTypes);
    readonly currentSelectedPledgeType$ = this.select((s) => s.currentSelectedPledgeType);

    constructor(private _http: HttpClient,
                private _pledgeTypeApiService: PledgeTypeClient,
                private _notificationService: NotificationService,
                resetRegistry: StateResetRegistry) {
        resetRegistry.register(() => this._state$.next(initialState));
    }

    // get Pledge Types (paged search — results are the caller's page state)
    getPledgeTypes(searchValue: string,
                   sortName: string,
                   descending: boolean,
                   limit: number,
                   offset: number): Observable<PagedListOfPledgeType> {
        const request = this._pledgeTypeApiService.searchPledgeTypes(
            searchValue,
            limit || 10,
            offset || 0,
            sortName || 'Name',
            null,
            descending
        ).pipe(share());
        request.subscribe({
            error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }
        });
        return request;
    }

    addPledgeType(pledgeType: PledgeType): Observable<PledgeType> {
        const request = this._pledgeTypeApiService.addPledgeType(pledgeType).pipe(share());
        this._notificationService.showSuccess('Attempting to save new Fund Type.');
        request.subscribe({
            next: (result) => {
                this.setCurrentSelectedPledgeType(result);
                this.refreshAllPledgeTypes(); // keep the app-wide cache fresh
                this._notificationService.showSuccess('Fund Type Successfully Created!');
            }, error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }, complete: () => {

            }
        });

        return request;
    }

    updatePledgeType(pledgeType: PledgeType): Observable<PledgeType> {
        const request = this._pledgeTypeApiService.updatePledgeType(pledgeType).pipe(share());
        this._notificationService.showSuccess('Attempting to update Fund Type.');
        request.subscribe({
            next: (result) => {
                this.setCurrentSelectedPledgeType(result);
                this.refreshAllPledgeTypes(); // keep the app-wide cache fresh
                this._notificationService.showSuccess('Fund Type Succesfully Updated!');
            }, error: () => {
                this._notificationService.showError('There was a problem updating this Fund Type.');
            }, complete: () => {

            }
        });
        return request;
    }

    deletePledgeType(id: string): Observable<boolean> {
        const request = this._pledgeTypeApiService.deletePledgeType(id).pipe(share());
        this._notificationService.showSuccess('Attempting to delete Fund Type.');
        request.subscribe({
            next: () => {
                this.refreshAllPledgeTypes(); // keep the app-wide cache fresh
                this._notificationService.showSuccess('Fund Type Successfully Deleted!');
            }, error: (err) => {
                this._notificationService.showError(getMessageFromException(err));
            }, complete: () => {

            }
        });

        return request;
    }

    getAllPledgeTypes(): Observable<PledgeType[]> {
        const request = this._pledgeTypeApiService.retrievePledgeTypes().pipe(share());
        request.subscribe({
            next: (result) => {
                this.setAllPledgeTypes(result);
            }, error: () => {
                this._notificationService.showError('There was an problem getting the Fund Types');
            }, complete: () => {

            }
        });
        return request;
    }

    retrieveFundAdministrativePledgeTypes(): Observable<PledgeType[]> {
        // 003-retire-ngrx (US2): the old store write into the applicationCounty
        // slice was never read back — callers use the returned observable.
        const request = this._pledgeTypeApiService.retrieveFundAdministrativeAgencyPledgeTypes().pipe(share());
        request.subscribe({
            next: () => {
            }, error: () => {
                this._notificationService.showError('There was an problem getting the Fund Administrative Fund Types');
            }
        });
        return request;
    }

    massAssignPledgeType(newPledgeTypeId: string, sourcePledgeTypeId: string): Observable<boolean> {
        const requestBody = MassAssignPledgeTypeParams.fromJS({
            newPledgeType: newPledgeTypeId,
            sourcePledgeType: sourcePledgeTypeId
        });

        const request = this._pledgeTypeApiService.massAssignPledgeType(requestBody).pipe(share());
        this._notificationService.showSuccess('Attempting to mass assign Fund Type.');
        request.subscribe({
            next: () => {
                this._notificationService.showSuccess('Fund Type mass assigned!');
            }, error: () => {
                this._notificationService.showError('There was a problem mass assigning the Fund Type.');
            }, complete: () => {

            }
        });
        return request;
    }


    setCurrentSelectedPledgeType(currentSelectedPledgeType: PledgeType): void {
        this.setCurrentSelectedPledgeTypeAction(currentSelectedPledgeType);
    }

    // getCurrentSelectedPledgeType(): PledgeType {// TODO: not used yet; on browser refresh
    //   return this.getCurrentSelectedPledgeTypeAction();
    // }

    getPledgeTypeById(id: string): void {
        this._pledgeTypeApiService.retrievePledgeType(id).subscribe((result) => {
            this.setCurrentSelectedPledgeTypeAction(result);
        });
    }

    setAllPledgeTypes(pledgeTypes: PledgeType[]): void {
        this.setAllPledgeTypesAction(pledgeTypes);
    }

    // -------- state transitions (former pledgeType actions) ----------
    private setCurrentSelectedPledgeTypeAction(currentPledgeType: PledgeType): void {
        this.patch({currentSelectedPledgeType: currentPledgeType});
    }

    setAllPledgeTypesAction(pledgeTypes: PledgeType[]): void {
        this.patch({allPledgeTypes: pledgeTypes});
    }

    // Only refetch after a mutation if something loaded the cache already —
    // otherwise the next consumer's load-if-empty fetch gets fresh data anyway.
    private refreshAllPledgeTypes(): void {
        if (this._state$.value.allPledgeTypes) {
            this.getAllPledgeTypes();
        }
    }

    // ---------------- State plumbing ----------------
    private select<T>(project: (state: PledgeTypeSliceState) => T): Observable<T> {
        return this._state$.pipe(map(project), distinctUntilChanged());
    }

    private patch(partial: Partial<PledgeTypeSliceState>): void {
        this._state$.next({...this._state$.value, ...partial});
    }
}
